<script setup lang="ts">
import NavBarDrawer from '../components/NavBarDrawer.vue';
import ButtonSecondary from '@/shared/components/button/ButtonSecondary.vue';
import PhoneIcon from '@/shared/assets/icon/phone.svg';
import EmailIcon from '@/shared/assets/icon/email.svg';
import WhatsAppIcon from '@/shared/assets/icon/whatsapp.svg';
import { COUNTRY_CODE } from '@/utils/constants';

defineProps<{ visible: boolean }>();
defineEmits<{ 'update:visible': [value: boolean] }>();

const regions = [
  {
    id: COUNTRY_CODE.Australia,
    name: 'Australia',
    officeHours: 'Monday to Thursday: 9am—5pm (AEST)</br> Friday: 9am—1pm (AEST)',
    phone: '+61 (0) 3 7038 0686',
    phoneLink: 'tel:+61370380686',
    whatsappLink: 'https://wa.me/61370380686',
    email: 'support@allumeenergy.com.au',
  },
  {
    id: COUNTRY_CODE['United Kingdom'],
    name: 'United Kingdom',
    officeHours: 'Monday to Friday: 9am—5pm (GMT)',
    phone: '+44 (0) 20 8156 2818',
    phoneLink: 'tel:+442081562818',
    whatsappLink: 'https://wa.me/442081562818',
    email: 'uk.support@allumeenergy.com',
  },
];
</script>

<template>
  <NavBarDrawer :visible="visible" @update:visible="$emit('update:visible', $event)">
    <h2 class="contact-us__title">Contact Us</h2>
    <p class="contact-us__subtitle">Need installation support?</p>
    <p class="contact-us__subtitle">Contact our local support centre.</p>

    <div v-for="region in regions" :key="region.id" class="contact-us__region">
      <h3 class="contact-us__region__name" :data-tn="`${region.id}-name`">{{ region.name }}</h3>
      <p class="contact-us__region__hours" :data-tn="`${region.id}-hours`" v-html="region.officeHours"></p>

      <div class="contact-us__cta-row">
          <span><PhoneIcon class="cta__svg-icon" /></span>
          <span>
            <a :href="region.phoneLink" class="cta-link" :data-tn="`${region.id}-phone`"> {{ region.phone }} </a>
           </span>
      </div>
      <div class="contact-us__cta-row">
          <span><EmailIcon class="cta__svg-icon" /></span>
          <span>
            <a :href="`mailto:${region.email}`" class="cta-link" :data-tn="`${region.id}-email`"> {{ region.email }} </a>
           </span>
      </div>
      <div class="contact-us__cta-row whatsapp-contact-container">
        <span>
          <a :href="region.whatsappLink" class="cta-link" :data-tn="`${region.id}-whatsapp-link`">
            <ButtonSecondary class="cta-button whatsapp-button" size="small">
              <template #iconRight>
                <WhatsAppIcon class="whatsapp-icon" />
              </template>
              <span class="whatsapp-button-text">Chat on WhatsApp</span>
            </ButtonSecondary>
          </a>
        </span>
      </div>
    </div>
  </NavBarDrawer>
</template>

<style lang="less" scoped>
@import '../../../assets/style/variables';

.contact-us {
  background-color: @color-background-dark;

  &__title {
    margin-bottom: 16px;
  }

  &__subtitle {
    margin-bottom: 0;
  }

  &__region {
    margin-top: 2rem;

    &__name {
      font-weight: @font-weight-bold !important;
      font-size: 1rem;
      margin: 0;
      margin-bottom: 4px;
    }

    &__hours {
      margin-bottom: 2px;
      font-size: 14px;
    }
  }
}

.contact-us__cta-row {
  margin-top: 0.65rem;
  margin-bottom: 0.65rem;
  display: flex;
  align-items: center;

  > span {
    display: inline-flex;
    align-items: center;
  }

  .cta__svg-icon {
    color: @color-primary;
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
  }

  .cta-link {
    text-decoration: none;
    color: @color-primary;
  }
}

.whatsapp-contact-container {
  .whatsapp-icon {
    width: 1rem;
    height: 1rem;
  }

  .whatsapp-button {
    height: 1.85rem;
    font-weight: @font-weight-regular;
    border-radius: @border-radius-medium;
  }

  .whatsapp-button-text {
    font-size: 1rem;
    padding-right: 1.5rem;
  }
}
</style>
